// React.
import React, { useContext, useEffect, useMemo, useState } from 'react';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import ChartPlotLine from "../../../types/ChartPlotLine";
import CombinedChanges from "../../../types/instance/CombinedChanges";

// Helper.
import {
    archiverUrl,
    fetchResults, fetchWithAuthorization,
    getConvertedTimeToUTC,
    highchartsCredits,
    stringFormatting
} from '../../../helpers/utils';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { useLicences } from "../../../context/LicenceContext";
import Helper from "../../../helpers/Helper";
import dayjs from "dayjs";
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {
    getInstanceTotalDuration,
    getStatementExecutions,
    getStatementTypeFromURL,
    StatementTypeInfo
} from "../../instances/tabs/utils";

// Components.
import WidgetCard from "../../../component/instance/widget";
import StatementSqlStatisticsTable from "../../../component/instance/table/StatementSqlStatisticsTable";
import EventsApi from "../../../api/instance/EventsApi";
import { CHART_DATA_TYPE } from "../../../component/Constants";
import ConditionalRender from "../../../helpers/ConditionalRender";

require("highcharts/highcharts-more")(Highcharts);

export default function SqlStatistics(props: {
    instance: InstanceTarget,
    filterParameters: string,
    statementId: string,
    batchStatementId?: string,
    applyPeriod: Function,
    statementType?: StatementTypeInfo,
}) {
    const { instance, statementId } = props;
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh
    const [loading, setLoading] = useState<boolean>(true);
    const pathname = window.location.pathname

    // Parse URL parameters for baseline metrics
    const urlParams = new URLSearchParams(window.location.search);
    const avgDurationParam = parseFloat(urlParams.get('avgduration') || '0');
    const baselineAvgDurationParam = parseFloat(urlParams.get('baselineavgduration') || '0');
    const baselineStdDevDurationParam = parseFloat(urlParams.get('baselinestddevduration') || '0');

    const [statementCombinedChanges, setStatementCombinedChanges] = useState<CombinedChanges[]>([]);
    // const [statementChartPlotBands, setStatementChartPlotBands] = useState<ChartPlotBand[]>([]);
    const [statementChartPlotLines, setStatementChartPlotLines] = useState<ChartPlotLine[]>([]);

    const { licences } = useLicences();
    const statementType = props.statementType || getStatementTypeFromURL()

    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);
    const [avgDurationSeries, setAvgDurationSeries] = useState<number[][]>([]);
    const [durationRange1Series, setDurationRange1Series] = useState<number[][]>([]);
    const [durationRange2Series, setDurationRange2Series] = useState<number[][]>([]);
    const [durationRange3Series, setDurationRange3Series] = useState<number[][]>([]);

    const [supportedStatistics, setSupportedStatistics] = useState<any[]>([]);
    const [statisticsToShow, setStatisticsToShow] = useState<any[]>([]);

    const [executions, setExecutions] = useState<number>(0);
    const [previousExecutions, setPreviousExecutions] = useState<number>(0);

    const [totalInstanceTime, setTotalInstanceTime] = useState<number>(0);
    const [totalPreviousInstanceTime, setTotalPreviousInstanceTime] = useState<number>(0);

    const timeSeriesPeak = useMemo(() => {
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const executionPeak = useMemo(() => {
        return Helper.getPeakValue(executionsSeries.map(i => i[1]))
    }, [executionsSeries])

    const getExecutions = async (instanceId: number) => {
        const executionsResult = await getStatementExecutions(instanceId, instance.type, statementId, timeRangeContext, props.filterParameters, statementType.isGroup, props.batchStatementId)
        setExecutions(executionsResult.data)
        setPreviousExecutions(executionsResult.previousData)
    }

    const getTotalTime = async (instanceId: number) => {
        const executionsTimeResult = await getInstanceTotalDuration(instanceId, timeRangeContext, instance.type, statementId, statementType.isGroup, props.batchStatementId)
        setTotalInstanceTime(executionsTimeResult.totalInstanceTime)
        setTotalPreviousInstanceTime(executionsTimeResult.totalPreviousInstanceTime)
    }


    const getChanges = async (instanceId: number) => {

        let allEvents: any[] = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instanceId),
            combinedChanges: CombinedChanges[] = [],
            chartPlotLines: ChartPlotLine[] = [];

        for (let index = 0; index < allEvents.length; index++) {
            const currentItem = allEvents[index];
            combinedChanges.push({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            });
        }

        // Set the sort order for the combined changes and events.
        combinedChanges.sort(function compare(a, b) {
            var aa: number = dayjs(a.timeslice).valueOf();
            var bb: number = dayjs(b.timeslice).valueOf();
            return bb - aa;
        });

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval)
        chartPlotLines = Helper.getChartPlotLines(timeRangeContext.getTimeRangeRedirectString(), combinedChanges, chartCategories, interval, false, statementId);
        // const chartPlotBands = Helper.getChartPlotbands(combinedChanges, chartCategories);

        setStatementCombinedChanges(combinedChanges)
        // setStatementChartPlotBands(chartPlotBands)
        setStatementChartPlotLines(chartPlotLines)
    }

    useEffect(() => {
        let isMounted = true;

        async function load() {
            try {
                await getExecutions(instance.id)
                await getTotalTime(instance.id)
                await getChanges(instance.id)
                setLoading(true);
                // const sqlHashString = getAPIString(statementId, props.batchStatementId)
                const sqlHashString = statementType.isGroup ? 'grouphash' : 'sqlhash'
                const results: any[][] = await fetchResults(
                    [

                        fetchWithAuthorization(archiverUrl(2) + `/sql/statistic/time?${timeRangeContext.getTimeRangeQueryString()}&id=${instance.id}&type=${instance.type}&${sqlHashString}=${props.batchStatementId || statementId}`)
                    ]);
                let executions: number[][] = []
                let duration: number[][] = []
                let avgDuration: number[][] = []
                let durationRange1: number[][] = []
                let durationRange2: number[][] = []
                let durationRange3: number[][] = []

                if (active && results[0].length) {
                    const supportedStatisticsList: any[][] = await fetchResults(
                        [
                            fetchWithAuthorization(archiverUrl(1) + `/sql/statistic/name?type=${props.instance.type}&collect=true`)
                        ]);

                    const filteredSupportedStatistics = supportedStatisticsList[0].filter(key => key !== 'duration' && key !== 'executions');
                    const stats: any[] = []

                    filteredSupportedStatistics.forEach(stat => {
                        stats.push({
                            name: stat,
                            data: results[0].map(item => [getConvertedTimeToUTC(item), item[stat]])
                        });
                    })

                    results[0].forEach(item => {
                        executions.push([getConvertedTimeToUTC(item), item.executions])
                        duration.push([getConvertedTimeToUTC(item), item.duration])


                        // Process avgduration and stddevduration for range charts
                        const timestamp = getConvertedTimeToUTC(item);
                        const avg = item.avgduration || 0;
                        const stddev = item.stddevduration || 0;


                        // Calculate range bounds for 1σ, 2σ, and 3σ, ensuring lower bounds don't go below 0
                        avgDuration.push([timestamp, avg]);
                        durationRange1.push([timestamp, Math.max(0, avg - stddev), avg + stddev]);
                        durationRange2.push([timestamp, Math.max(0, avg - 2 * stddev), avg + 2 * stddev]);
                        durationRange3.push([timestamp, Math.max(0, avg - 3 * stddev), avg + 3 * stddev]);
                    })
                    setSupportedStatistics(stats)
                    setExecutionsSeries(executions)
                    setTimeSeries(duration)
                    setAvgDurationSeries(avgDuration)
                    setDurationRange1Series(durationRange1)
                    setDurationRange2Series(durationRange2)
                    setDurationRange3Series(durationRange3)
                }
                setLoading(false);
            } catch (x: any) {
                console.log(x.message);
                setLoading(false);
            }

            return true;
        }

        let active: boolean = true;
        if (instance && isMounted) {
            setLoading(true);

            const getDataPromise = load();

            getDataPromise.then(() => {
                if (isMounted) {
                    setLoading(false);
                }
            });
        }

        return () => {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [timeRangeContext, instance.id, instance.type, pathname]);

    const timeChart = useMemo(() => {
        const timeChartOptions = chartOptionsGranularity(props.applyPeriod, [
            {
                type: 'column',
                color: '#69d1bf',
                data: timeSeries,
                name: 'Duration',
                zIndex: 1,
                tooltip: { valueSuffix: 'ms' }
            }, {
                type: 'spline',
                color: '#ff853d',
                minHeight: 3,
                data: executionsSeries,
                name: 'Executions',
                zIndex: 1,
                yAxis: 1
            }
        ], timeSeriesPeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            plotLines: statementChartPlotLines,
            // plotBands: statementChartPlotBands,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
            options={timeChartOptions} />;

    }, [timeRangeContext, timeSeries, timeSeriesPeak, executionsSeries]);

    const avgDurationPeak = useMemo(() => {
        // Get peak from the 3σ range (which will be the highest)
        const peakFromRange = durationRange3Series.length > 0
            ? Math.max(...durationRange3Series.map(i => i[2]))
            : 0;
        return peakFromRange;
    }, [durationRange3Series]);

    const avgDurationChart = useMemo(() => {
        const avgDurationChartOptions = chartOptionsGranularity(props.applyPeriod, [
            {
                type: 'arearange',
                color: '#ffcccc', // Pale red for 3σ
                data: durationRange3Series,
                name: '±3 Std Dev',
                lineWidth: 0,
                fillOpacity: 0.3,
                zIndex: 0,
                marker: { enabled: false }
            },
            {
                type: 'arearange',
                color: '#ffd699', // Pale orange for 2σ
                data: durationRange2Series,
                name: '±2 Std Dev',
                lineWidth: 0,
                fillOpacity: 0.4,
                zIndex: 1,
                marker: { enabled: false }
            },
            {
                type: 'arearange',
                color: '#c8e6c9', // Pale green for 1σ
                data: durationRange1Series,
                name: '±1 Std Dev',
                lineWidth: 0,
                fillOpacity: 0.5,
                zIndex: 2,
                marker: { enabled: false }
            },
            {
                type: 'line',
                color: '#69d1bf',
                data: avgDurationSeries,
                name: 'Average Duration',
                zIndex: 3,
                marker: {
                    fillColor: 'white',
                    lineWidth: 2,
                    lineColor: '#69d1bf'
                }
            }
        ], avgDurationPeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            plotLines: statementChartPlotLines,
            tooltip: {
                shared: true,
                useHTML: true,
                outside: true,
                className: 'highcharts-tooltip-single',
                borderWidth: 0,
                padding: 0,
                shadow: false,
                backgroundColor: null,
                borderRadius: 0,
                borderColor: 'none',
                formatter: function (this: any) {
                    const dateHeader = Helper.formatDate(this.x).replace(" at", ",");
                    let data = '';

                    if (this.points) {
                        for (let i = 0; i < this.points.length; i++) {
                            const pt = this.points[i];
                            const ptColor = pt.color || pt.series.color;

                            if (pt.point.low !== undefined && pt.point.high !== undefined) {
                                const low = Helper.getTimeInEnglish(pt.point.low);
                                const high = Helper.getTimeInEnglish(pt.point.high);
                                data += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${ptColor}"></div>${pt.series.name} Lower:</div> <span style="padding-left: 20px" class="value">${low || '0ms'}</span></div>`;
                                data += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${ptColor}"></div>${pt.series.name} Upper:</div> <span style="padding-left: 20px" class="value">${high}</span></div>`;
                            } else if (pt.y !== undefined) {
                                const value = Helper.getTimeInEnglish(pt.y);
                                data += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${ptColor}"></div>${pt.series.name}:</div> <span style="padding-left: 20px" class="value">${value}</span></div>`;
                            }
                        }
                    }

                    return `<div class="chart-tooltip-new"><div class="tooltip-header">${dateHeader}</div>${data}</div>`;
                }
            },
        })

        // Ensure chart type is not overridden - remove it from the chart object
        if (avgDurationChartOptions?.chart) {
            const { type, ...chartWithoutType } = avgDurationChartOptions.chart as any;
            avgDurationChartOptions.chart = chartWithoutType;
        }

        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
            options={avgDurationChartOptions} />;

    }, [timeRangeContext, avgDurationSeries, durationRange1Series, durationRange2Series, durationRange3Series, avgDurationPeak, statementChartPlotLines, licences, props.applyPeriod]);

    // Check if we have baseline parameters from URL
    const hasBaselineParams = avgDurationParam > 0 || baselineAvgDurationParam > 0;

    // Create flat line data for baseline metrics
    const avgDurationFlatLine = useMemo(() => {
        if (avgDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [item[0], avgDurationParam]);
        }
        return [];
    }, [avgDurationSeries, avgDurationParam]);

    const baselineAvgDurationFlatLine = useMemo(() => {
        if (baselineAvgDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [item[0], baselineAvgDurationParam]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam]);

    // Create flat shaded areas for baseline standard deviations (ABOVE baseline)
    const baselineFlatRange1Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    const baselineFlatRange2Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + 2 * baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    const baselineFlatRange3Series = useMemo(() => {
        if (baselineAvgDurationParam > 0 && baselineStdDevDurationParam > 0 && avgDurationSeries.length > 0) {
            return avgDurationSeries.map(item => [
                item[0],
                baselineAvgDurationParam,
                baselineAvgDurationParam + 3 * baselineStdDevDurationParam
            ]);
        }
        return [];
    }, [avgDurationSeries, baselineAvgDurationParam, baselineStdDevDurationParam]);

    // Calculate peak for baseline comparison chart
    const baselineComparisonPeak = useMemo(() => {
        const peaks = [
            avgDurationParam,
            baselineAvgDurationParam + 3 * baselineStdDevDurationParam,
            ...avgDurationSeries.map(i => i[1])
        ].filter(v => v > 0);
        return peaks.length > 0 ? Math.max(...peaks) : 0;
    }, [avgDurationSeries, avgDurationParam, baselineAvgDurationParam, baselineStdDevDurationParam]);

    // Baseline Comparison Chart
    const baselineComparisonChart = useMemo(() => {
        if (!hasBaselineParams || avgDurationSeries.length === 0) {
            return null;
        }

        const series: any[] = [];

        // Add baseline flat standard deviation ranges (ABOVE baseline)
        // Using progressively lighter shades based on baseline line color #ff9800
        if (baselineFlatRange3Series.length > 0) {
            series.push({
                type: 'arearange',
                color: '#fff3e0', // Lightest orange for baseline 3σ
                data: baselineFlatRange3Series,
                name: 'Baseline +3σ',
                lineWidth: 0,
                fillOpacity: 0.6,
                zIndex: 0,
                marker: { enabled: false }
            });
        }

        if (baselineFlatRange2Series.length > 0) {
            series.push({
                type: 'arearange',
                color: '#ffe0b2', // Light orange for baseline 2σ
                data: baselineFlatRange2Series,
                name: 'Baseline +2σ',
                lineWidth: 0,
                fillOpacity: 0.7,
                zIndex: 1,
                marker: { enabled: false }
            });
        }

        if (baselineFlatRange1Series.length > 0) {
            series.push({
                type: 'arearange',
                color: '#ffcc80', // Medium orange for baseline 1σ (closest to line color)
                data: baselineFlatRange1Series,
                name: 'Baseline +1σ',
                lineWidth: 0,
                fillOpacity: 0.8,
                zIndex: 2,
                marker: { enabled: false }
            });
        }

        // Add baseline average duration flat line
        if (baselineAvgDurationFlatLine.length > 0) {
            series.push({
                type: 'line',
                color: '#ff9800', // Orange for baseline
                data: baselineAvgDurationFlatLine,
                name: 'Baseline Avg Duration',
                zIndex: 3,
                lineWidth: 2,
                marker: { enabled: false }
            });
        }

        // Add current average duration flat line
        if (avgDurationFlatLine.length > 0) {
            series.push({
                type: 'line',
                color: '#9c27b0', // Purple for current avg
                data: avgDurationFlatLine,
                name: 'Current Avg Duration',
                zIndex: 4,
                lineWidth: 2,
                marker: { enabled: false }
            });
        }

        // Add actual average duration from data
        series.push({
            type: 'line',
            color: '#69d1bf',
            data: avgDurationSeries,
            name: 'Average Duration',
            zIndex: 5,
            marker: {
                fillColor: 'white',
                lineWidth: 2,
                lineColor: '#69d1bf'
            }
        });

        const chartOptions = chartOptionsGranularity(props.applyPeriod, series, baselineComparisonPeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            plotLines: statementChartPlotLines,
            tooltip: {
                shared: true,
                useHTML: true,
                outside: true,
                className: 'highcharts-tooltip-single',
                borderWidth: 0,
                padding: 0,
                shadow: false,
                backgroundColor: null,
                borderRadius: 0,
                borderColor: 'none',
                formatter: function (this: any) {
                    const dateHeader = Helper.formatDate(this.x).replace(" at", ",");
                    let data = '';

                    if (this.points) {
                        for (let i = 0; i < this.points.length; i++) {
                            const pt = this.points[i];
                            const ptColor = pt.color || pt.series.color;

                            if (pt.point.low !== undefined && pt.point.high !== undefined) {
                                const low = Helper.getTimeInEnglish(pt.point.low);
                                const high = Helper.getTimeInEnglish(pt.point.high);
                                data += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${ptColor}"></div>${pt.series.name}:</div> <span style="padding-left: 20px" class="value">${low} - ${high}</span></div>`;
                            } else if (pt.y !== undefined) {
                                const value = Helper.getTimeInEnglish(pt.y);
                                data += `<div class="d-flex justify-content-between"><div><div class="dot" style="background-color: ${ptColor}"></div>${pt.series.name}:</div> <span style="padding-left: 20px" class="value">${value}</span></div>`;
                            }
                        }
                    }

                    return `<div class="chart-tooltip-new"><div class="tooltip-header">${dateHeader}</div>${data}</div>`;
                }
            },
        });

        // Ensure chart type is not overridden
        if (chartOptions?.chart) {
            const { type, ...chartWithoutType } = chartOptions.chart as any;
            chartOptions.chart = chartWithoutType;
        }

        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
            options={chartOptions} />;

    }, [hasBaselineParams, avgDurationSeries, avgDurationFlatLine, baselineAvgDurationFlatLine, baselineFlatRange1Series, baselineFlatRange2Series, baselineFlatRange3Series, baselineComparisonPeak, timeRangeContext, statementChartPlotLines, licences, props.applyPeriod]);

    const getChart = ((statisticData: { name: string, data: [] }) => {
        const chartOptions = chartOptionsGranularity(props.applyPeriod, [
            {
                type: 'column', data: statisticData.data, name: statisticData.name, zIndex: 2
            }
        ], null, {
            credits: { enabled: highchartsCredits(licences) },
            timeRangeContext: timeRangeContext,
            plotLines: statementChartPlotLines,
            // plotBands: statementChartPlotBands,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
            options={chartOptions} />;

    });

    const renderedList = useMemo(() => {
        const sortedList = supportedStatistics.sort((a, b) => a.name.localeCompare(b.name));
        return sortedList?.map((statistic: any[any], i) =>
            <div key={i}>
                <input className="form-check-input" type="checkbox" id={`option_${i}`}
                    checked={statisticsToShow.find(item => item.name === statistic.name)}
                    onChange={() => updateSqlExtendedOptionsList(statistic)}
                />
                <label htmlFor={`option_${i}`}>{stringFormatting(statistic.name)}</label>
            </div>)

    }, [supportedStatistics, statisticsToShow])

    const updateSqlExtendedOptionsList = (value: any[any]) => {
        let updatedList = JSON.parse(JSON.stringify(statisticsToShow))
        // @ts-ignore
        const exist = updatedList?.find(item => item.name === value.name)
        if (exist) {
            const filteredList = updatedList.filter((item: any) => item.name !== exist.name);
            setStatisticsToShow(filteredList)
        } else {
            updatedList.push(value)
            setStatisticsToShow(updatedList)
        }
    }

    const checkUncheckAll = (checkState: boolean) => {
        setStatisticsToShow(checkState ? supportedStatistics : [])
    }

    const changes = statementCombinedChanges.filter(item => item.change);
    const events = statementCombinedChanges.filter(item => item.custom);
    const alerts = statementCombinedChanges.filter(item => item.alert);

    return (<>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
            <WidgetCard
                name="Total Time"
                type='time'
                iconClassName="time"
                icon="fa-stopwatch"
                value={totalInstanceTime}
                previous={totalPreviousInstanceTime}
                cardTooltipText="The total time spent"
            />

            <WidgetCard
                name="Executions"
                iconClassName="executions"
                icon="fa-chart-line"
                value={executions}
                previous={previousExecutions}
                cardTooltipText="The total number of executions"
            />

            <WidgetCard
                name="Average Time"
                iconClassName="averagetime"
                icon="fa-clock"
                value={executions > 0 ? totalInstanceTime / executions : 0}
                previous={previousExecutions > 0 ? totalPreviousInstanceTime / previousExecutions : undefined}
                cardTooltipText="The average time per execution"
            />

            <WidgetCard
                name="Events"
                events={{
                    alerts: alerts.length,
                    changes: changes.length,
                    custom: events.length
                }}
            />
        </div>

        <div className="row row-cols-1">
            <div id="statements" className="tab-pane fade show active" role="tabpanel"
                aria-labelledby="statements-tab">

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                        Duration (ms) and Executions
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                            data-bs-target={"#collapse-duration"} aria-expanded="false"
                            aria-controls={"collapse-duration"} />
                        <div className="btn-group float-end dropdown ">
                            {supportedStatistics.length ?
                                <button type="button" id="filter1" className="btn btn-dropdown dropdown-toggle"
                                    data-bs-toggle="dropdown" aria-expanded="false">More Statistics
                                </button> : ''}
                            <div id="dropdown-menu" className="row g-0 dropdown-menu dropdown-menu-scroll"
                                aria-labelledby="filter" onClick={(e) => e.stopPropagation()}>
                                <div className='select-all'>
                                    <span onClick={() => checkUncheckAll(true)}>All</span><label
                                        className='separator'>|</label><span
                                            onClick={() => checkUncheckAll(false)}>None</span>
                                </div>
                                <div className='statistics-list'>
                                    {renderedList}
                                </div>
                            </div>

                        </div>
                    </div>

                    <div id={"collapse-duration"} className="card-body collapse show">

                        <p className="chart-peak">
                            <React.Fragment>Peak
                                Time: {Helper.getTimeInEnglish(timeSeriesPeak)}</React.Fragment>,&nbsp;
                            <React.Fragment>Peak
                                Executions: {executionPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</React.Fragment>
                        </p>
                        <br />

                        <ConditionalRender if={loading && !isAutorefresh}>
                            <div className="w-100 text-center time-chart-center text-muted mt-3">
                                <div className="loader spinner chartSpinner"></div>
                                <p>Loading data...</p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={isAutorefresh || !loading}>
                            {timeChart}
                        </ConditionalRender>

                    </div>
                </div>

                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                        Average Duration with Standard Deviation
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                            data-bs-target={"#collapse-avg-duration"} aria-expanded="false"
                            aria-controls={"collapse-avg-duration"} />
                    </div>

                    <div id={"collapse-avg-duration"} className="card-body collapse show">
                        <ConditionalRender if={loading && !isAutorefresh}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={isAutorefresh || !loading}>
                            {avgDurationSeries.length > 0 && (
                                <>
                                    <p className="chart-peak">
                                        <React.Fragment>Peak Average Duration: {Helper.getTimeInEnglish(avgDurationPeak)}</React.Fragment>
                                    </p>
                                    <br />
                                    {avgDurationChart}
                                </>
                            )}
                            {avgDurationSeries.length === 0 && !loading && (
                                <p className="text-muted">No average duration data available</p>
                            )}
                        </ConditionalRender>
                    </div>
                </div>

                {hasBaselineParams && (
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            Baseline Comparison
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target={"#collapse-baseline-comparison"} aria-expanded="false"
                                aria-controls={"collapse-baseline-comparison"} />
                        </div>

                        <div id={"collapse-baseline-comparison"} className="card-body collapse show">
                            <ConditionalRender if={loading && !isAutorefresh}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={isAutorefresh || !loading}>
                                {avgDurationSeries.length > 0 && (
                                    <>
                                        <p className="chart-peak">
                                            {avgDurationParam > 0 && (
                                                <React.Fragment>Current Avg: {Helper.getTimeInEnglish(avgDurationParam)}</React.Fragment>
                                            )}
                                            {avgDurationParam > 0 && baselineAvgDurationParam > 0 && ', '}
                                            {baselineAvgDurationParam > 0 && (
                                                <React.Fragment>Baseline Avg: {Helper.getTimeInEnglish(baselineAvgDurationParam)}</React.Fragment>
                                            )}
                                            {baselineStdDevDurationParam > 0 && (
                                                <React.Fragment>, Baseline Std Dev: {Helper.getTimeInEnglish(baselineStdDevDurationParam)}</React.Fragment>
                                            )}
                                        </p>
                                        <br />
                                        {baselineComparisonChart}
                                    </>
                                )}
                                {avgDurationSeries.length === 0 && !loading && (
                                    <p className="text-muted">No duration data available</p>
                                )}
                            </ConditionalRender>
                        </div>
                    </div>
                )}

                {statisticsToShow.map(stat =>
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            {stat.name.replaceAll('_', ' ')}
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target={`#${stat.name}`} aria-expanded="false"
                                aria-controls={stat.name} />
                        </div>
                        <div id={stat.name} className="card-body collapse show">
                            {getChart(stat)}
                        </div>
                    </div>)}

                <StatementSqlStatisticsTable instance={props.instance} statementId={statementId} batchStatementId={props.batchStatementId} filterParameters={props.filterParameters} statementType={statementType}/>
            </div>
        </div>
    </>
    )
}
